/** Static, illustrative community feed for the public landing page. No member data is fetched. */
import React from "react";

export const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="story-accent inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.16em]">
    <span aria-hidden className="h-px w-7 bg-current" />
    {children}
  </span>
);

const PhoneShell: React.FC<{ children: React.ReactNode; label: string }> = ({
  children,
  label,
}) => (
  <div className="story-artwork relative mx-auto w-full max-w-[330px]">
    {/* Side buttons, behind the frame so they read as protruding from it. */}
    <span aria-hidden className="absolute -left-[3px] top-[108px] h-8 w-[3px] rounded-l bg-[#2c3634]" />
    <span aria-hidden className="absolute -left-[3px] top-[152px] h-12 w-[3px] rounded-l bg-[#2c3634]" />
    <span aria-hidden className="absolute -left-[3px] top-[208px] h-12 w-[3px] rounded-l bg-[#2c3634]" />
    <span aria-hidden className="absolute -right-[3px] top-[168px] h-16 w-[3px] rounded-r bg-[#2c3634]" />

    {/* Metal frame: a light top edge and a dark base sell the curvature. */}
    <div
      className="relative rounded-[3.1rem] bg-gradient-to-b from-[#5c6b68] via-[#2f3b39] to-[#1c2523] p-[3px] shadow-[0_36px_84px_rgba(23,32,38,0.34)]"
      aria-label={label}
    >
      <div className="rounded-[3rem] bg-[#0d1211] p-[9px]">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#f9f7f1]">
          {/* Dynamic island */}
          <span
            aria-hidden
            className="absolute left-1/2 top-[10px] z-10 flex h-[26px] w-[92px] -translate-x-1/2 items-center justify-end gap-2 rounded-full bg-black pr-3"
          >
            <span className="h-2 w-2 rounded-full bg-[#1c2b33]" />
          </span>

          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pb-1 pt-[14px] text-[11px] font-semibold text-slate-800">
            <span className="w-[52px]">9:41</span>
            <span className="flex w-[52px] items-center justify-end gap-[3px]" aria-hidden>
              {[3, 5, 7, 9].map((h) => (
                <span key={h} className="w-[3px] rounded-sm bg-slate-800" style={{ height: h }} />
              ))}
              <span className="relative ml-[3px] h-[9px] w-[16px] rounded-[3px] border border-slate-800/80">
                <span className="absolute inset-[1.5px] right-[4px] rounded-[1px] bg-slate-800" />
              </span>
            </span>
          </div>

          <div className="px-5 pb-6 pt-2">
            {children}
            <span
              aria-hidden
              className="mx-auto mt-5 block h-[5px] w-[110px] rounded-full bg-slate-900/25"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FEED = [
  {
    room: "Alumni association",
    ago: "12m",
    head: "Five members volunteered for the careers clinic.",
    sub: "One more speaker is needed.",
    tint: "from-[#a8bbb7] to-[#405258]",
  },
  {
    room: "Former colleagues",
    ago: "1h",
    head: "Someone is moving cities this autumn.",
    sub: "Three people offered advice on where to live.",
    tint: "from-[#d3b7a7] to-[#70584f]",
  },
  {
    room: "Your club",
    ago: "3h",
    head: "Thursday\u2019s run moves to 6:30.",
    sub: "Same meeting point.",
    tint: "from-[#c8bf9d] to-[#6a6247]",
  },
];

export const PhoneFrame: React.FC = () => (
  <PhoneShell label="Illustrative community feed, ending in a caught-up card">
    <p className="font-display text-[26px] leading-none text-slate-900">What changed</p>
    <div className="mt-4">
      {FEED.map((u) => (
        <div key={u.room} className="border-t border-slate-200 py-4">
          <div className="flex items-center gap-2 text-[10px] text-slate-600">
            <span className={`h-6 w-6 rounded-full bg-gradient-to-br ${u.tint}`} />
            <span>
              {u.room} · {u.ago}
            </span>
          </div>
          <p className="mt-2 text-[13px] font-medium leading-snug text-slate-900">{u.head}</p>
          <p className="mt-1 text-[11px] text-slate-600">{u.sub}</p>
        </div>
      ))}
    </div>
    <div className="mt-3 rounded-2xl bg-[#dce7e3] px-4 py-5 text-center">
      <p className="font-display text-lg text-slate-900">You&rsquo;re all caught up</p>
      <p className="mt-0.5 text-[11px] text-slate-600">Go live the rest of your day.</p>
    </div>
  </PhoneShell>
);
